import React, { useMemo } from 'react';
import { useJobs } from 'src/hooks/useJobs';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { IconFileText, IconPlus } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { RfiDto } from 'src/api/FiretrolApiClient';
import { useRfisList } from 'src/api/queries/rfis/useRfiQueries';
import { useRfiLogPdf } from 'src/api/queries/rfis/useRfiMutations';
import { IContact } from 'src/api/models/Organization';
import { resolveWorkflowButtonColor } from 'src/utils';
import { WorkflowCreateButton } from 'src/app/workflows/WorkflowCreateButton';
import { workflowStatusColumn } from 'src/app/workflows/workflowStatusColumn';
import { WorkflowStatusPanel } from 'src/design/molecules/WorkflowStatusPanel';
import { WorkflowLogModal } from 'src/app/workflows/WorkflowLogModal';
import { useWorkflowStatusFilters } from 'src/app/workflows/useWorkflowStatusFilters';
import { useWorkflowStatusSegments } from 'src/app/workflows/useWorkflowStatusSegments';
import { useWorkflowLogPdf } from 'src/app/workflows/useWorkflowLogPdf';
import { htmlFirstLine } from 'src/app/workflows/htmlFirstLine';
import { RFI_STATUS_COLORS } from 'src/app/rfis/rfiStatusColors';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';

const getColumns = (): MRT_ColumnDef<RfiDto>[] => [
  {
    accessorKey: 'rfiNumber',
    header: 'RFI Number',
    size: 150,
  },
  workflowStatusColumn<RfiDto>({ size: 180 }),
  {
    id: 'description',
    header: 'Description',
    size: 260,
    accessorFn: (row) => htmlFirstLine(row.question),
  },
  {
    id: 'daysOpen',
    header: 'Days Open',
    size: 120,
    accessorFn: (row) => (row.daysOpen != null ? String(row.daysOpen) : ''),
  },
  {
    id: 'contractor',
    header: 'Contractor',
    size: 200,
    accessorFn: (row) => row.submittedToContact?.name || '',
  },
  {
    id: 'assignee',
    header: 'Assignee',
    size: 180,
    accessorFn: (row) => row.reporter?.fullName || '',
  },
];

export const RFIs: React.FC = () => {
  const { selectedJob } = useJobs();
  const { data, isLoading, isFetching, isError, refetch } = useRfisList(
    selectedJob?.id,
  );
  const rfis = React.useMemo<RfiDto[]>(() => data ?? [], [data]);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    covered,
    filters,
    selectableStatuses,
    statusFilter,
    setStatusFilter,
    filteredItems,
  } = useWorkflowStatusFilters(
    rfis,
    selectedJob?.rfiWorkflow,
    Object.keys(RFI_STATUS_COLORS),
  );
  const segments = useWorkflowStatusSegments(
    rfis,
    filters,
    covered,
    selectedJob?.rfiWorkflow,
    RFI_STATUS_COLORS,
  );

  const [logOpen, setLogOpen] = React.useState(false);
  const [logStatuses, setLogStatuses] = React.useState<string[]>([]);
  const [logContact, setLogContact] = React.useState<IContact | null>(null);
  const logPdf = useRfiLogPdf();
  const { file: logFile, loading: logLoading } = useWorkflowLogPdf({
    open: logOpen,
    jobId: selectedJob?.id,
    statuses: logStatuses,
    contact: logContact,
    fetchPdf: (jobId, statuses, contactId) =>
      logPdf.mutateAsync({ jobId, statuses, contactId }),
    logLabel: 'Failed to load RFI log PDF',
    onError: () => setLogOpen(false),
  });
  const logUrl = usePdfBlobUrl(logFile?.data);

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'job-rfis',
    columns: columns,
    defaultSorting: [{ id: 'rfiNumber', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ViewActionIcon
            onClick={() =>
              navigate(`/jobs/${row.original.jobId}/rfis/${row.original.id}`)
            }
            label="View RFI"
          />
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: filteredItems,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: filteredItems.length,
    isLoading,
    isFetching,
    isError,
    onRefresh: () => {
      refetch();
    },
  });

  if (!selectedJob) return null;

  return (
    <div>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(selectedJob, undefined, 'RFIs'),
        }}
        title="RFIs"
        helpText="Manage RFIs (Requests for Information) for this job. Create new RFIs, view existing ones, and track their status through the approval workflow."
        actions={
          selectedJob.rfiWorkflow ? (
            <WorkflowCreateButton
              workflow={selectedJob.rfiWorkflow}
              color={resolveWorkflowButtonColor(
                selectedJob.rfiWorkflow.createButtonColor,
              )}
              leftSection={<IconPlus />}
              onClick={() => navigate(`${location.pathname}/submit/rfi`)}
            >
              {selectedJob.rfiWorkflow.createButtonText || 'Create RFI'}
            </WorkflowCreateButton>
          ) : undefined
        }
      />

      <Group mb="md">
        <SecondaryButton
          leftSection={<IconFileText />}
          onClick={() => setLogOpen(true)}
        >
          RFI Log
        </SecondaryButton>
      </Group>

      {!selectedJob.rfiWorkflowId && (
        <Alert color="red" title="Missing RFI Workflow" mb="md">
          Default workflow does not exist. Please contact an administrator.
        </Alert>
      )}

      <WorkflowStatusPanel
        segments={segments}
        value={statusFilter}
        onChange={setStatusFilter}
      />

      {table}

      <WorkflowLogModal
        opened={logOpen}
        onClose={() => setLogOpen(false)}
        title="RFI Log"
        pdfTitle="RFI Log PDF"
        statusOptions={selectableStatuses}
        statuses={logStatuses}
        onStatusChange={setLogStatuses}
        customerNumber={selectedJob.customerNumber?.trim() ?? ''}
        contact={logContact}
        onContactChange={setLogContact}
        logUrl={logUrl}
        logLoading={logLoading}
      />
    </div>
  );
};
